import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAdminUser } from './useAdminUser';
import { 
  useGetTasksQuery,
  useGetArchivedTasksQuery,
  useGetAvailableAssigneesQuery,
} from '@/store/api/admin/tasksApi';
import { 
  ITaskItemAttributes, 
  ITaskTableRow, 
  ITaskFilterModel, 
  ITaskSortModel,
  TaskViewType,
  TASK_VIEW_TYPES,
  TASK_SORT_OPTIONS,
  TASK_STATUS_CONFIG,
  TaskStatusType,
} from '@/types/admin/tasks';
import { IPaginationModel } from '@/types/admin/dataTable';
import { createEnhancedError } from '@/utils/admin/errorHandling';
import { useTasksDefaults } from '@/hooks/admin/tasks/useTasksDefaults';
import { useDataTableState } from '@/contexts/admin/DataTableContext';
import { useVisibleTasks, useTableRows, useGroupedTasks, useUniqueAssignees } from '@/hooks/admin/tasks/useTasksDerivations';
import { useTasksActions } from '@/hooks/admin/tasks/useTasksActions';

export const useTasks = () => {
  // Get admin user data (including selected property)
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  const { defaultFilterModel, defaultSortModel, defaultPaginationModel } = useTasksDefaults();
  
  // Local state for view toggle; table-like state via global slice
  const [view, setView] = useState<TaskViewType>(TASK_VIEW_TYPES.LIST);
  const table = useDataTableState('admin:tasks');
  const searchQuery = table.searchQuery;
  const filterModel = ((table.filterModel as unknown) as ITaskFilterModel) || defaultFilterModel;
  const sortModel = ((table.sortModel as unknown) as ITaskSortModel[]) || defaultSortModel;
  const paginationModel = (table.paginationModel as IPaginationModel) || defaultPaginationModel;
  
  // Quick add task inputs for each status
  const [quickAddInputs, setQuickAddInputs] = useState<Record<string, string>>({});
  
  // Retry state
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // API queries
  const { data: tasksData, isLoading: isTasksLoading, error: tasksError, refetch: refetchTasks } = useGetTasksQuery(
    { filterModel, paginationModel, selectedPropertyId: selectedPropertyId!, searchQuery },
    { skip: !selectedPropertyId || view !== TASK_VIEW_TYPES.LIST, refetchOnMountOrArgChange: true },
  );
  const { data: archivedTasksData, isLoading: isArchivedTasksLoading, error: archivedTasksError, refetch: refetchArchivedTasks } = useGetArchivedTasksQuery(
    { selectedPropertyId: selectedPropertyId!, month: filterModel.month!, year: filterModel.year!, filterModel, searchQuery },
    { skip: !selectedPropertyId || view !== TASK_VIEW_TYPES.ARCHIVED || !filterModel.month || !filterModel.year, refetchOnMountOrArgChange: true },
  );
  const { data: availableAssignees, isLoading: isAssigneesLoading } = useGetAvailableAssigneesQuery(
    { propertyId: selectedPropertyId! },
    { skip: !selectedPropertyId },
  );

  // Mutations moved to useTasksActions

  // Determine current data and loading state based on view
  const currentData = view === TASK_VIEW_TYPES.ARCHIVED ? archivedTasksData : tasksData;
  const isLoading = view === TASK_VIEW_TYPES.ARCHIVED ? isArchivedTasksLoading : isTasksLoading;
  const currentError = view === TASK_VIEW_TYPES.ARCHIVED ? archivedTasksError : tasksError;

  // Enhanced error handling
  const enhancedError = useMemo(() => {
    if (!currentError) return null;
    return createEnhancedError(currentError as any, 'tasks');
  }, [currentError]);

  // Apply client-side filtering/sorting via derivation hook
  const visibleTasks: ITaskItemAttributes[] = useVisibleTasks(currentData as any, view, searchQuery, filterModel as any, sortModel);

  // Transform tasks data to table rows
  const tableRows: ITaskTableRow[] = useTableRows(visibleTasks);

  // Group tasks by status for Kanban view
  const groupedTasks = useGroupedTasks(visibleTasks, view);

  // Get unique assignees for filtering based on base tasks for current view (matches admin-web)
  const uniqueAssignees = useUniqueAssignees(currentData as any, view);

  // Update handlers
  const updateView = useCallback((newView: TaskViewType) => {
    setView(newView);
    setRetryCount(0); // Reset retry count when changing views

    // Reset filters (except month/year) and sort for parity with admin-web
    const now = new Date();
    const ensureMonth = (filterModel as any)?.month ?? (now.getMonth() + 1);
    const ensureYear = (filterModel as any)?.year ?? now.getFullYear();
    table.setFilter({
      ...(filterModel as any),
      // Always preserve search
      search: (filterModel as any)?.search,
      // Reset these like admin-web
      assignedTo: undefined,
      assignedToNot: undefined as any,
      creator: [],
      creatorNot: undefined as any,
      status: undefined as any,
      statusNot: undefined as any,
      // Seed month/year defaults specifically for archived view
      ...(newView === TASK_VIEW_TYPES.ARCHIVED
        ? { month: ensureMonth, year: ensureYear }
        : {}),
    } as any);
    // Match admin-web: list => newestCreationDate, archived => newestArchiveDate
    table.setSort([
      newView === TASK_VIEW_TYPES.ARCHIVED
        ? ({ field: 'newestArchiveDate', sort: 'desc' } as ITaskSortModel)
        : ({ field: 'newestCreationDate', sort: 'desc' } as ITaskSortModel),
    ] as any);
    table.setPagination({ ...defaultPaginationModel });
  }, [table, defaultPaginationModel, filterModel]);

  const updateSearchQuery = useCallback((query: string) => {
    table.setSearch(query);
    if (paginationModel.page !== 0) table.setPagination({ ...paginationModel, page: 0 });
  }, [table, paginationModel]);

  const updateFilterModel = useCallback((newFilter: Partial<ITaskFilterModel>) => {
    table.setFilter({ ...(filterModel as any), ...(newFilter as any) });
    if (paginationModel.page !== 0) table.setPagination({ ...paginationModel, page: 0 });
  }, [table, filterModel, paginationModel]);

  const updateSortModel = useCallback((newSort: ITaskSortModel[]) => {
    table.setSort(newSort as any);
    if (paginationModel.page !== 0) table.setPagination({ ...paginationModel, page: 0 });
  }, [table, paginationModel]);

  // Ensure default sort is applied on first mount for the current view
  useEffect(() => {
    const hasSort = Array.isArray(sortModel) && sortModel.length > 0;
    if (!hasSort) {
      const defaultForView: ITaskSortModel = (view === TASK_VIEW_TYPES.ARCHIVED)
        ? ({ field: 'newestArchiveDate', sort: 'desc' } as ITaskSortModel)
        : ({ field: 'newestCreationDate', sort: 'desc' } as ITaskSortModel);
      table.setSort([defaultForView] as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const updatePaginationModel = useCallback((newPagination: IPaginationModel) => {
    table.setPagination(newPagination);
  }, [table]);

  // Quick add task handlers
  const updateQuickAddInput = useCallback((status: TaskStatusType, text: string) => {
    setQuickAddInputs(prev => ({ ...prev, [status]: text }));
  }, []);

  const clearQuickAddInput = useCallback((status: TaskStatusType) => {
    setQuickAddInputs(prev => ({ ...prev, [status]: '' }));
  }, []);

  const { handleQuickCreateTask: baseQuickCreate, handleArchiveTask: baseArchive, handleDuplicateTask: baseDuplicate, handleDeleteTask: handleDeleteTask, isQuickCreating } = useTasksActions(selectedPropertyId);
  const handleQuickCreateTask = useCallback(async (title: string, taskStatus: TaskStatusType) => {
    await baseQuickCreate(title, taskStatus);
    clearQuickAddInput(taskStatus);
  }, [baseQuickCreate, clearQuickAddInput]);

  // Task action handlers
  const handleArchiveTask = useCallback(async (taskId: number, isArchived: boolean = true) => {
    await baseArchive(taskId, isArchived);
  }, [baseArchive]);

  const handleDuplicateTask = useCallback(async (taskId: number) => {
    await baseDuplicate(taskId, (id) => visibleTasks.find(t => t.id === id));
  }, [baseDuplicate, visibleTasks]);

  // handleDeleteTask provided by useTasksActions

  // Pagination handlers
  const hasNextPage = useMemo(() => {
    if (!currentData) return false;
    const totalPages = Math.ceil(currentData.count / paginationModel.pageSize);
    return paginationModel.page < totalPages - 1;
  }, [currentData, paginationModel]);

  const hasPreviousPage = useMemo(() => {
    return paginationModel.page > 0;
  }, [paginationModel]);

  const goToNextPage = useCallback(() => {
    if (hasNextPage) {
      table.setPagination({ ...paginationModel, page: paginationModel.page + 1 });
    }
  }, [hasNextPage, table, paginationModel]);

  const goToPreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      table.setPagination({ ...paginationModel, page: paginationModel.page - 1 });
    }
  }, [hasPreviousPage, table, paginationModel]);

  // Retry functionality
  const canRetry = retryCount < maxRetries;
  
  const manualRetry = useCallback(() => {
    if (canRetry) {
      setRetryCount(prev => prev + 1);
      if (view === TASK_VIEW_TYPES.ARCHIVED) {
        refetchArchivedTasks();
      } else {
        refetchTasks();
      }
    }
  }, [canRetry, view, refetchTasks, refetchArchivedTasks]);

  // Refresh data
  const refresh = useCallback(() => {
    setRetryCount(0);
    if (view === TASK_VIEW_TYPES.ARCHIVED) {
      refetchArchivedTasks();
    } else {
      refetchTasks();
    }
  }, [view, refetchTasks, refetchArchivedTasks]);

  // Auto-retry on error
  useEffect(() => {
    if (enhancedError && retryCount < maxRetries) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        refresh();
      }, Math.pow(2, retryCount) * 1000); // Exponential backoff
      
      return () => clearTimeout(timer);
    }
  }, [enhancedError, retryCount, maxRetries, refresh]);

  return {
    // Data
    data: currentData || { data: [], count: 0 },
    visibleTasks,
    tableRows,
    groupedTasks,
    availableAssignees: availableAssignees || [],
    uniqueAssignees,
    
    // Loading states
    isLoading: isLoading || isQuickCreating,
    isAssigneesLoading,
    
    // Error handling
    enhancedError,
    retryCount,
    canRetry,
    manualRetry,
    
    // UI state
    view,
    searchQuery,
    filterModel,
    sortModel,
    paginationModel,
    quickAddInputs,
    
    // UI handlers
    updateView,
    updateSearchQuery,
    updateFilterModel,
    updateSortModel,
    updatePaginationModel,
    
    // Pagination
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    
    // Quick add functionality
    updateQuickAddInput,
    clearQuickAddInput,
    handleQuickCreateTask,
    
    // Task actions
    handleArchiveTask,
    handleDuplicateTask,
    handleDeleteTask,
    
    // Refresh
    refresh,
    
    // Constants for UI
    sortOptions: TASK_SORT_OPTIONS,
    statusConfig: TASK_STATUS_CONFIG,
  };
};
